import React from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Box, Paper, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import RouterIcon from '@mui/icons-material/Router';
import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BusinessIcon from '@mui/icons-material/Business';

function Settings() {
    const { t } = useTranslation();

    const [isAdmin, setIsAdmin] = useState(false);
    const [canManageDevices, setCanManageDevices] = useState(false);
    const [isIotAdmin, setIsIotAdmin] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchAuthSession().then(session => {
            const groups = session.tokens?.idToken?.payload?.['cognito:groups'] ?? [];
            setIsAdmin(groups.includes('user_management'));
            setCanManageDevices(groups.includes('device_management'));
            setIsIotAdmin(groups.includes('device_management'));
        });
    }, []);

    const isProfileActive = pathname === '/settings' || pathname === '/settings/profile';
    const isDevicesActive = pathname.startsWith('/settings/devices');
    const isDeviceTypesActive = pathname === '/settings/device-types';
    const isCompaniesActive = pathname === '/settings/companies';
    const isUsersActive = pathname === '/settings/users';
    const isIotDevicesActive = pathname === '/settings/iot-devices';

    // Build the ordered list of visible tabs for mobile
    const tabs = [
        { label: t('settings.profile'), to: '/settings/profile', icon: <PersonOutlineIcon />, active: isProfileActive },
        ...(canManageDevices ? [
            { label: t('settings.devices'), to: '/settings/devices', icon: <InboxIcon />, active: isDevicesActive },
            { label: t('settings.deviceTypes'), to: '/settings/device-types', icon: <DevicesOtherIcon />, active: isDeviceTypesActive },
        ] : []),
        ...(isIotAdmin ? [
            { label: t('settings.iotDevices'), to: '/settings/iot-devices', icon: <RouterIcon />, active: isIotDevicesActive },
        ] : []),
        ...(isAdmin ? [
            { label: t('settings.users'), to: '/settings/users', icon: <ManageAccountsIcon />, active: isUsersActive },
            { label: t('settings.companies'), to: '/settings/companies', icon: <BusinessIcon />, active: isCompaniesActive },
        ] : []),
    ];

    const activeTabIndex = tabs.findIndex(tab => tab.active);

    const Sidebar = (
        <Box sx={{ width: 250 }} role="navigation" aria-label="settings submenu">
            <List>
                <ListItem disablePadding>
                    <ListItemButton component={NavLink} to="/settings/profile" selected={isProfileActive}
                        sx={{ '&.active': { backgroundColor: 'action.selected' } }}>
                        <ListItemIcon><PersonOutlineIcon /></ListItemIcon>
                        <ListItemText primary={t('settings.profile')} />
                    </ListItemButton>
                </ListItem>
                {canManageDevices && (
                    <ListItem disablePadding>
                        <ListItemButton component={NavLink} to="/settings/devices" selected={isDevicesActive}
                            sx={{ '&.active': { backgroundColor: 'action.selected' } }}>
                            <ListItemIcon><InboxIcon /></ListItemIcon>
                            <ListItemText primary={t('settings.devices')} />
                        </ListItemButton>
                    </ListItem>
                )}
                {canManageDevices && (
                    <ListItem disablePadding>
                        <ListItemButton component={NavLink} to="/settings/device-types" selected={isDeviceTypesActive}
                            sx={{ '&.active': { backgroundColor: 'action.selected' } }}>
                            <ListItemIcon><DevicesOtherIcon /></ListItemIcon>
                            <ListItemText primary={t('settings.deviceTypes')} />
                        </ListItemButton>
                    </ListItem>
                )}
                {isIotAdmin && (
                    <ListItem disablePadding>
                        <ListItemButton component={NavLink} to="/settings/iot-devices" selected={isIotDevicesActive}
                            sx={{ '&.active': { backgroundColor: 'action.selected' } }}>
                            <ListItemIcon><RouterIcon /></ListItemIcon>
                            <ListItemText primary={t('settings.iotDevices')} />
                        </ListItemButton>
                    </ListItem>
                )}
            </List>
            {isAdmin && (
                <>
                    <Divider />
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton component={NavLink} to="/settings/users"
                                sx={{ '&.active': { backgroundColor: 'action.selected' } }}>
                                <ListItemIcon><ManageAccountsIcon /></ListItemIcon>
                                <ListItemText primary={t('settings.users')} />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton component={NavLink} to="/settings/companies" selected={isCompaniesActive}
                                sx={{ '&.active': { backgroundColor: 'action.selected' } }}>
                                <ListItemIcon><BusinessIcon /></ListItemIcon>
                                <ListItemText primary={t('settings.companies')} />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </>
            )}
        </Box>
    );

    if (isMobile) {
        return (
            <Box sx={{ pt: 2 }}>
                <Tabs
                    value={activeTabIndex === -1 ? 0 : activeTabIndex}
                    onChange={(_, i) => navigate(tabs[i].to)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 2,
                        '& .MuiTab-root': { minWidth: 0, px: 2, fontSize: '0.8rem' },
                    }}
                >
                    {tabs.map((tab) => (
                        <Tab key={tab.to} label={tab.label} icon={tab.icon} iconPosition="start" />
                    ))}
                </Tabs>
                <Paper elevation={2} sx={{ p: 2 }}>
                    <Outlet />
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                {Sidebar}
                <Box sx={{ flex: 1 }}>
                    <Paper elevation={4} sx={{ p: 2, mt: 2, textAlign: 'left' }}>
                        <Outlet />
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
}

export default Settings;
